import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import { FiHeadphones, FiMusic, FiSettings } from "react-icons/fi";

function useOnScreen(options) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, options);

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, [ref, options]);

  return [ref, visible];
}

function FAQItem({ q, a, idx, open, setOpen }) {
  const expanded = open === idx;
  return (
    <div className={`faq-item ${expanded ? "open" : ""}`}>
      <button
        className="faq-q"
        aria-expanded={expanded}
        onClick={() => setOpen(expanded ? null : idx)}
      >
        <span>{q}</span>
        <span className={`chev ${expanded ? "up" : ""}`}>^</span>
      </button>
      <div className={`faq-a ${expanded ? "open" : ""}`}>
        <p style={{ whiteSpace: 'pre-line' }}>{a}</p>
      </div>
    </div>
  );
}

export default function App() {
  const [open, setOpen] = useState(null);
  const [philosophyRef, philosophyVisible] = useOnScreen({ threshold: 0.6 });
  const [faqRef, faqVisible] = useOnScreen({ threshold: 0.6 });
  const [heroRef, heroVisible] = useOnScreen({ threshold: 0.1 });

  const faqs = [
    {
      q: "What is Nomelo?",
      a: "Nomelo was born from one belief: you shouldn't need to choose between being in the flow, and enjoying what you love. We remold the music to deepen presence, and help you achieve your tasks."
    },
    {
      q: "Hooooow does it work?",
      a: "It learns your patterns, and adapts songs you love into flowscapes matching your tasks — reimagining the piece in a way that suits you, and keeps its artistic essence."
    },
    {
      q: "What makes Nomelo better than other focus apps?",
      a: "Music is art, not sterile science.\n\nInstead of just giving binaural beats without soul, we shape songs to keep artistic beauty. Meanwhile, Nomelo learns and feels through your mood & rhythms; The same playlist that can carry you through mindful mornings at 9:00 am becomes an upbeat study sprint at 7:00 pm. (that's how I study best lol)\n\nWhat you have will always feel like yours, and carry you through the winds ~ and back."
    },
    {
      q: "No two listens are ever the same.. Why?",
      a: "Every listen is contextual. Our system reacts to your session time of day, prior listening, and preferences so each playback is subtly unique."
    },
    {
      q: "Can I use my own music?",
      a: "You can upload your own music or listen to the catalogue that exists. Indie artists are supported, we luv new music & creativity :)"
    }
  ];

  return (
    <div className="page">
      {/* background effects */}
      <div className="stars" aria-hidden="true" />
      <div className="flicker-stars" aria-hidden="true">
        {[...Array(8)].map((_, i) => <span key={i} />)}
      </div>
      <div className="twinkle-stars" aria-hidden="true">
        {[...Array(5)].map((_, i) => <span key={i} />)}
      </div>
      <div className="blob b1" aria-hidden="true" />
      <div className="blob b2" aria-hidden="true" />

      {/* NAV */}
      <header className="nav">
        <div className="max">
          <div className="nav-inner">
            <div className="nav-brand">
              <span className="nomelo-text">Nomelo</span>
            </div>
            <nav className="nav-links">
              <a href="#about">About us</a>
              <a href="#faqs">FAQs</a>
              <a href="#contact">Contact Us</a>
              <a href="#signup" className="btn-secondary">Sign Up</a>
            </nav>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="hero" ref={heroRef}>
        <div className="max">
          <h1 className={`hero-title fade-in ${heroVisible ? "animate" : ""}`}>Nomelo</h1>
          <p className="hero-sub">
            Transform your favourite music to help you focus, relax and sleep.<br />
            Meticulously created.
          </p>
          <a className="cta-primary" href="https://docs.google.com/forms/d/1JAC-v4k_Ocn7VrQ0QXZ8vMWABteX3v04e-do_Ak8088/edit" target="_blank" rel="noopener noreferrer" aria-label="Start free trial">Try it Free (Limited time only!)</a>
        </div>
      </section>

      {/* WHY */}
      <section id="about" className="why">
        <div className="max">
          <h2 className="section-title">
            Why Students &amp; Creators love Nomelo
          </h2>

          <div className="cards">
            <article className="card">
              <div className="card-icon">
                <FiHeadphones />
              </div>
              <h3>Unlock Flow State</h3>
              <p>Your brain stays creative without distraction.</p>
            </article>

            <article className="card">
              <div className="card-icon">
                <FiMusic />
              </div>
              <h3>Tailored to You</h3>
              <p>From lo-fi to lyrical, we adapt your favourite songs to your task.</p>
            </article>

            <article className="card">
              <div className="card-icon">
                <FiSettings />
              </div>
              <h3>Tools You Already Use</h3>
              <p>Seamlessly syncs with Notion, Pomodoro, and more.</p>
            </article>
          </div>
        </div>
      </section>

      {/* BELIEFS */}
      <section className="beliefs" ref={philosophyRef}>
        <div className="max">
          <h3 className={`section-sub ${philosophyVisible ? "animate" : ""}`}>The Philosophy</h3>
          <div className="belief-grid">
            <div className="belief">
              <h4><strong>Mission Statement</strong></h4>
              <p>
                We believe music should not pull focus away from what matters.
                It should be a companion that enhances your studying, working,
                or resting. Not distract, but rebuild.
              </p>
            </div>
            <div className="belief">
              <h4><strong>Vision Statement</strong></h4>
              <p>
                To humanize AI sound design and empower you to work, study, and
                rest building a foundation for a future that supports focus, not
                distraction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faqs" className="faq" ref={faqRef}>
        <div className="max">
          <h2 className={`section-title fade-in ${faqVisible ? "animate" : ""}`}>FAQ</h2>
          <div className="faq-wrap">
            {faqs.map((f, i) => (
              <FAQItem key={i} idx={i} q={f.q} a={f.a} open={open} setOpen={setOpen} />
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer id="contact" className="footer">
        <div className="max f-row">
          <div className="foot-left">
            <div className="brand-mini">@Nomelo • 2025 – Onwards</div>
            <div className="links">
              <a href="https://nomelo.app" target="_blank" rel="noreferrer">nomelo.app</a>
              <a href="https://x.com/nomelomusic" target="_blank" rel="noreferrer">x.com/nomelomusic</a>
              <a href="https://youtube.com/nomelo" target="_blank" rel="noreferrer">youtube.com/nomelo</a>
            </div>
          </div>
          <div className="foot-right">
            <span className="dot sm" />
            <span className="dot sm" />
            <span className="dot sm" />
          </div>
        </div>
      </footer>
    </div>
  );
}
